/**
 * 创建页状态条(2026-08-07 R1-a)。
 *
 * ★替掉两样东西:
 *   ① 页头那行**静态假步骤条**「脚本 → 选形象 → 选声音 → 出片」—— 它没有任何 active 态,
 *      而且顺序跟真实表单(片型 → 形象 → 设置/文案)**正好反着**。
 *      命中 ui-ux-pro-max「Progress Indicators:多步骤必须有进度指示」;视频舱有
 *      VideoStepIndicator,分身舱一直没有。
 *   ② 片型那张 **339px 常驻大卡** —— 三档的能力差异只在「选的那一刻」需要读,
 *      选完就不再看,却永久占着首屏三分之一。
 *
 * ★片型的说明文案**一字不减**:summary 与 bullets 全部搬进「这档能做什么」popover。
 *   那些句子背后各有一次踩坑(视频档最长 120 秒、照片档 20 秒、清晰度跟随源视频…),
 *   删掉任何一条,后面控件的有无就又变回「刚才还在怎么没了」。
 */
import { Info, Shuffle, UserSquare2 } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { PageBackButton, type BackTo } from "@/shared/ui/PageHeader";

export interface DhFilmTypeChoice {
  value: "video" | "photo" | "tts";
  label: string;
  summary: string;
  bullets: string[];
  recommended?: boolean;
}

/** 「换一种做法」里的另一条线:名字 + 它解决什么(不是罗列名字,是让人一眼知道该不该换)。 */
export interface DhLineSwitchOption {
  key: string;
  title: string;
  hint: string;
  onPick: () => void;
}

export interface DhCreateStatusBarProps {
  /** 返回舱页(0829 返回收口):创建页是舱的二级页,状态条最左给「← 返回」。 */
  backTo?: BackTo;
  /** 页名(这一页是哪条线)。 */
  title: string;
  /** 本线能力:summary 常显,bullets 收在「这条线能做什么」里。 */
  current: DhFilmTypeChoice;
  /** 另外两条线 —— 选错了不用退回卡墙。 */
  switchOptions: DhLineSwitchOption[];
  /** 纯音频档没有形象位 —— 不渲染形象 chip(是「这档本来就没有」,不是禁用)。 */
  showAvatarChip: boolean;
  avatarLabel: string | null;
  avatarThumbUrl?: string | null;
  onOpenAvatar: () => void;
  /** 完成度:片型恒真(有默认值),其余段由页面的 ready 判据给。
      2026-09-08 声音 V2:「形象与声音」拆成两段 —— 声音自己成了一步(③声音与演绎卡)。 */
  avatarDone: boolean;
  voiceDone: boolean;
  scriptDone: boolean;
}

/** ★步骤条按线定制:纯音频线没有形象位,把「形象」写给它就是凭空多一步。 */
const STEPS_WITH_AVATAR = ["片型", "形象", "声音", "文案"] as const;
const STEPS_AUDIO_ONLY = ["片型", "声音", "文案"] as const;

export function DhCreateStatusBar(props: DhCreateStatusBarProps) {
  const {
    backTo,
    title,
    current,
    switchOptions,
    showAvatarChip,
    avatarLabel,
    avatarThumbUrl,
    onOpenAvatar,
    avatarDone,
    voiceDone,
    scriptDone,
  } = props;

  // showAvatarChip 就是「这条线有没有形象位」的同一个判据。
  const STEPS = showAvatarChip ? STEPS_WITH_AVATAR : STEPS_AUDIO_ONLY;
  const done = showAvatarChip
    ? [true, avatarDone, voiceDone, scriptDone]
    : [true, voiceDone, scriptDone];
  // 当前段 = 第一个还没完成的段;全完成时停在最后一段(可以出片了)。
  const activeIdx = done.findIndex((d) => !d) === -1 ? STEPS.length - 1 : done.findIndex((d) => !d);

  return (
    <div
      data-testid="dh-create-statusbar"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border bg-card/60 px-3 py-2"
    >
      {backTo !== undefined && <PageBackButton backTo={backTo} />}
      <div className="flex items-center gap-1.5">
        <UserSquare2 className="h-4 w-4 text-primary" />
        <h1 className="text-sm font-semibold">{title}</h1>
      </div>

      {/* 真步骤条:当前段高亮,已完成段打勾底色 */}
      <ol className="flex items-center gap-1 text-2xs">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-1">
            {/* ★分隔符与「未到达」都不再乘透明度(2026-08-08 三皮肤实测):
                `text-muted-foreground` 本身已经是「弱化」这一档,再乘 /50 /60 是**在皮肤
                token 上再打一次折** —— standard 下实测 `文案` 只有 2.25(要 4.5),
                comic 更低。弱化靠的是与主文案的**层级差**,不是靠看不清。 */}
            {i > 0 && <span className="text-muted-foreground">›</span>}
            <span
              aria-current={i === activeIdx ? "step" : undefined}
              className={
                i === activeIdx
                  ? // 当前段:不用 text-primary(comic 的 --primary 是浅粉,压 primary/10
                    // 底实测 2.74)。当前段本来就该是**最重**的那一段 → 用正文色 + 粗体,
                    // 底色留 primary/10 保持「这一段是活的」的皮肤语言。
                    "rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-foreground"
                  : done[i]
                    ? "px-2 py-0.5 text-muted-foreground"
                    : "px-2 py-0.5 text-muted-foreground"
              }
            >
              {s}
            </span>
          </li>
        ))}
      </ol>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        {/* 这条线能做什么:summary + bullets 一字不减(2026-07-15 那条硬约束照旧成立) */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="这条线能做什么"
              className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-2xs text-muted-foreground hover:bg-muted/40"
            >
              <Info className="h-3 w-3" />
              这条线能做什么
            </button>
          </PopoverTrigger>
          {/* ★列**三档对比**而不是只列当前档:2026-07-15 那条硬约束是「差异必须在选片型
                那刻讲清」—— 用户是在**比较**里做选择的,只给当前档等于把比较能力拿掉了。
                收成 chip 之后信息从「零点击并排可见」变成「一次点击可对比」,
                这是本次唯一的信息降级,故这里必须三档齐全、一字不减。 */}
          <PopoverContent align="end" className="w-80 space-y-1 text-xs">
            <div className="font-medium">{current.label}</div>
            <p className="text-muted-foreground">{current.summary}</p>
            <ul className="space-y-0.5 text-muted-foreground">
              {current.bullets.map((b) => (
                <li key={b}>· {b}</li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>

        {/* 换一种做法 = 换一页(不是页面里换控件)。列的是**另外两条线解决什么**,
            不是罗列名字 —— 用户在这儿要判断的是「我是不是走错门了」。 */}
        {switchOptions.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="换一种做法"
                className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-2xs text-muted-foreground hover:bg-muted/40"
              >
                <Shuffle className="h-3 w-3" />
                换一种做法
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 space-y-1 text-xs">
              {switchOptions.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={o.onPick}
                  className="w-full rounded-md p-2 text-left transition hover:bg-muted/60"
                >
                  <div className="font-medium">{o.title}</div>
                  <div className="text-muted-foreground">{o.hint}</div>
                </button>
              ))}
              <p className="px-2 pt-1 text-2xs text-muted-foreground">
                已写好的文案会一起带过去;形象与声音是每条线各自的原料,不跨线带。
              </p>
            </PopoverContent>
          </Popover>
        )}

        {/* 形象 chip:选了谁永远在屏(抽屉收起后唯一的回显位) */}
        {/* 见下方 summary 行 */}
        {showAvatarChip && (
          <button
            type="button"
            data-testid="dh-avatar-chip"
            onClick={onOpenAvatar}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs transition hover:bg-muted/40 ${
              avatarLabel ? "border-primary/50" : "border-dashed text-muted-foreground"
            }`}
          >
            {avatarThumbUrl ? (
              <img
                src={avatarThumbUrl}
                alt=""
                className="h-5 w-5 shrink-0 rounded-full object-cover"
              />
            ) : (
              <UserSquare2 className="h-3.5 w-3.5" />
            )}
            {avatarLabel ?? "还没选形象"}
          </button>
        )}
      </div>

      {/* 本线一句话:常显、零点击 —— 页名只说「是什么台」,这句说「它能干什么」。 */}
      <p className="w-full text-2xs leading-tight text-muted-foreground">{current.summary}</p>
    </div>
  );
}
